import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "The Voice", href: "#voice" },
  { label: "Credits", href: "#credits" },
  { label: "Repertoire", href: "#repertoire" },
  { label: "Media", href: "#media" },
];

export const LogoMark = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
    <defs>
      <linearGradient id="hp-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#FF1493" />
        <stop offset="0.55" stopColor="#FF5E13" />
        <stop offset="1" stopColor="#00E5FF" />
      </linearGradient>
    </defs>
    <rect width="64" height="64" rx="14" fill="#0B0A12" />
    <rect x="2.5" y="2.5" width="59" height="59" rx="12" fill="none" stroke="url(#hp-grad)" strokeWidth="3" />
    <text x="32" y="43" textAnchor="middle" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontSize="28" fill="url(#hp-grad)">
      HP
    </text>
  </svg>
);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-[0_8px_40px_-12px_rgba(255,20,147,0.25)]" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16 h-16 lg:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo-link">
          <LogoMark />
          <span className="font-mono text-xs sm:text-sm tracking-[0.35em] text-white">
            HULDA<span className="text-hpink">PEREZ</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-hmut hover:text-white transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#booking"
            data-testid="nav-booking-cta"
            className="rounded-full bg-hpink hover:bg-horange transition-colors duration-300 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-white glow-pink"
          >
            Book Hulda
          </a>
        </nav>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-white/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-mono text-xs uppercase tracking-[0.25em] text-hmut hover:text-white"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#booking"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-booking-cta"
                className="rounded-full bg-hpink px-5 py-3 text-center font-mono text-xs uppercase tracking-[0.2em] font-bold"
              >
                Book Hulda
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
